/**
 * Screen-level art (shop background, speech bubble) and all the UI widgets:
 * top buttons (sound / star / refresh / bell), ingredient tiles, the coin
 * price-board and slot-card, and the earnings jar. Styled to match the
 * storybook reference.
 */
import { UI, INK } from '../palette.ts';
import {
  svgDoc,
  circle,
  ellipse,
  path,
  rect,
  polygon,
  line,
  group,
  defs,
  linear,
  url,
} from '../primitives.ts';
import { flowerMark } from './foods.ts';

/** A heart centered at (cx,cy), `s` = half-width in px. */
function heart(cx: number, cy: number, s: number, fill: string): string {
  const k = s / 10;
  const d =
    `M ${cx} ${cy - s * 0.2} ` +
    `C ${cx - 3 * k} ${cy - 7 * k} ${cx - 12 * k} ${cy - 9 * k} ${cx - 10 * k} ${cy - k} ` +
    `C ${cx - 9 * k} ${cy + 4 * k} ${cx - 2 * k} ${cy + 7 * k} ${cx} ${cy + 10 * k} ` +
    `C ${cx + 2 * k} ${cy + 7 * k} ${cx + 9 * k} ${cy + 4 * k} ${cx + 10 * k} ${cy - k} ` +
    `C ${cx + 12 * k} ${cy - 9 * k} ${cx + 3 * k} ${cy - 7 * k} ${cx} ${cy - s * 0.2} Z`;
  return path(d, { fill, stroke: 'none' });
}

// ── Shop background ────────────────────────────────────────────────────────────
export function background(W: number, H: number): string {
  const skyG = linear('skyG', [[0, UI.skyTop], [1, UI.skyBottom]]);

  const sky = rect(0, 0, W, H, 0, { fill: url('skyG'), stroke: 'none' });

  // Soft clouds.
  const cloud = (cx: number, cy: number, s: number): string =>
    group(
      ellipse(0, 6, 52, 30, { fill: UI.cloudShade, stroke: 'none' }) +
        ellipse(0, 0, 54, 30, { fill: UI.cloud, stroke: 'none' }) +
        ellipse(-42, 8, 32, 22, { fill: UI.cloud, stroke: 'none' }) +
        ellipse(42, 8, 32, 22, { fill: UI.cloud, stroke: 'none' }),
      `translate(${cx} ${cy}) scale(${s})`,
    );
  const clouds = cloud(240, 250, 1) + cloud(1010, 300, 0.85) + cloud(700, 200, 0.7);

  // Hedge band (bumpy green) along the horizon.
  const horizon = H * 0.56;
  const hedgeBumps: string[] = [];
  for (let x = -10; x < W + 60; x += 70) {
    hedgeBumps.push(circle(x, horizon, 50, { fill: UI.hedge, stroke: 'none' }));
    hedgeBumps.push(ellipse(x - 8, horizon - 22, 22, 14, { fill: UI.hedgeLight, stroke: 'none', opacity: 0.7 }));
  }
  const hedge =
    rect(-10, horizon, W + 20, 90, 0, { fill: UI.hedgeDark, stroke: 'none' }) + hedgeBumps.join('');

  // White picket fence.
  const fenceY = horizon - 4;
  const pickets: string[] = [];
  for (let x = 30; x < W; x += 56) {
    pickets.push(
      path(
        `M ${x} ${fenceY + 70} L ${x} ${fenceY + 14} L ${x + 16} ${fenceY} L ${x + 32} ${fenceY + 14} L ${x + 32} ${fenceY + 70} Z`,
        { fill: UI.fence, stroke: UI.fenceShade, strokeWidth: 3 },
      ),
    );
  }
  const rails =
    rect(-10, fenceY + 24, W + 20, 12, 4, { fill: UI.fence, stroke: UI.fenceShade, strokeWidth: 3 }) +
    rect(-10, fenceY + 48, W + 20, 12, 4, { fill: UI.fence, stroke: UI.fenceShade, strokeWidth: 3 });
  const fence = pickets.join('') + rails;

  // A few flowers tucked along the hedge.
  const flowers = [180, 520, 880, 1160]
    .map((x, i) => group(flowerMark(0, 0, 14), `translate(${x} ${horizon + 30}) scale(${i % 2 ? 0.9 : 1.1})`))
    .join('');

  // Wood counter.
  const counterTopY = H * 0.6;
  const counterTop = rect(-10, counterTopY, W + 20, 28, 14, { fill: UI.counterTop, stroke: 'none' });
  const counterFront = rect(-10, counterTopY + 22, W + 20, H - counterTopY, 0, {
    fill: UI.counter,
    stroke: 'none',
  });
  const seams: string[] = [];
  for (let x = 140; x < W; x += 220) {
    seams.push(line(x, counterTopY + 28, x, H, { stroke: UI.counterShade, strokeWidth: 4, opacity: 0.5 }));
  }

  // Bottom ingredient shelf: a panel with a pink scalloped top trim.
  const shelfY = H * 0.73;
  const shelfPanel = rect(-10, shelfY, W + 20, H - shelfY, 0, { fill: UI.counterTop, stroke: 'none' });
  const scallopTrim: string[] = [];
  const sr = 34;
  for (let x = 0; x <= W + sr; x += sr * 2) {
    scallopTrim.push(circle(x, shelfY, sr, { fill: UI.shelfBorder, stroke: 'none' }));
  }
  const trimBar = rect(-10, shelfY - 16, W + 20, 22, 0, { fill: UI.shelfBorder, stroke: 'none' });

  // Awning across the top.
  const awning = buildAwning(W);

  // Center sign board.
  const sign = buildSign(W);

  return svgDoc(
    W,
    H,
    defs(skyG) +
      sky +
      clouds +
      hedge +
      fence +
      flowers +
      counterTop +
      counterFront +
      seams.join('') +
      trimBar +
      scallopTrim.join('') +
      shelfPanel +
      // shelf trim drawn again ON TOP of panel edge so the bumps sit on the trim
      trimBar +
      scallopTrim.join('') +
      awning +
      sign,
  );
}

function buildAwning(W: number): string {
  const valance = rect(-10, 0, W + 20, 40, 0, { fill: UI.awningDark, stroke: 'none' });
  const stripeW = 64;
  const topY = 36;
  const botY = 150;
  const stripes: string[] = [];
  const bumps: string[] = [];
  let i = 0;
  for (let x = -10; x < W + stripeW; x += stripeW, i++) {
    const c = i % 2 === 0 ? UI.awning : UI.awningStripe;
    stripes.push(rect(x, topY, stripeW + 1, botY - topY, 0, { fill: c, stroke: 'none' }));
    bumps.push(circle(x + stripeW / 2, botY, stripeW * 0.52, { fill: c, stroke: 'none' }));
  }
  const lip = rect(-10, topY - 6, W + 20, 12, 0, { fill: UI.awningDark, stroke: 'none' });
  return valance + stripes.join('') + bumps.join('') + lip;
}

function buildSign(W: number): string {
  const cx = W / 2;
  const w = 360;
  const h = 120;
  const x = cx - w / 2;
  const plaque = rect(x, 6, w, h, 28, { fill: UI.sign, stroke: UI.signEdge, strokeWidth: 6 });
  // little scalloped crown on top
  const crown: string[] = [];
  for (let cxx = x + 36; cxx < x + w - 20; cxx += 44) {
    crown.push(circle(cxx, 6, 18, { fill: UI.sign, stroke: UI.signEdge, strokeWidth: 5 }));
  }
  // mini ice cream
  const iceX = cx;
  const iceY = 70;
  const mini =
    polygon([[iceX - 18, iceY + 6], [iceX + 18, iceY + 6], [iceX, iceY + 48]], { fill: UI.cone, strokeWidth: 4 }) +
    circle(iceX, iceY - 2, 25, { fill: UI.flowerPink, strokeWidth: 4 }) +
    circle(iceX - 12, iceY - 22, 17, { fill: UI.flowerPink, strokeWidth: 4 }) +
    circle(iceX + 12, iceY - 22, 17, { fill: UI.flowerPink, strokeWidth: 4 }) +
    circle(iceX, iceY - 36, 14, { fill: UI.flowerPink, strokeWidth: 4 }) +
    [
      [iceX - 8, iceY - 8, '#7bd389'],
      [iceX + 10, iceY - 12, '#5bc0eb'],
      [iceX - 2, iceY - 26, '#ffd23f'],
    ]
      .map(([dx, dy, col]) => circle(dx as number, dy as number, 3.2, { fill: col as string, stroke: 'none' }))
      .join('');
  const hearts = heart(x + 42, 66, 16, UI.heart) + heart(x + w - 42, 66, 16, UI.heart);
  return crown.join('') + plaque + hearts + mini;
}

// ── Speech bubble (empty; the order is drawn inside as sprites) ─────────────────
export function bubble(): string {
  const w = 360;
  const h = 290;
  const body = rect(12, 12, w - 24, 222, 38, { fill: UI.bubble, stroke: UI.bubbleEdge });
  const tail = path(`M ${w / 2 - 30} 230 L ${w / 2 - 4} 282 L ${w / 2 + 28} 230 Z`, {
    fill: UI.bubble,
    stroke: UI.bubbleEdge,
  });
  // Re-cover the seam where the tail meets the body so the outline reads as one shape.
  const seam = rect(16, 16, w - 32, 214, 34, { fill: UI.bubble, stroke: 'none' });
  return svgDoc(w, h, body + tail + seam);
}

// ── Reward / button star (with a happy little face) ─────────────────────────────
export function star(): string {
  const body = path(
    'M100,46 L114,84 L156,86 L123,112 L135,153 L100,129 L65,153 L77,112 L44,86 L86,84 Z',
    { fill: UI.star, stroke: UI.starShade, strokeWidth: 6 },
  );
  const eyes = circle(88, 104, 4.5, { fill: INK, stroke: 'none' }) + circle(112, 104, 4.5, { fill: INK, stroke: 'none' });
  const mouth = path('M90,116 q10,8 20,0', { fill: 'none', stroke: INK, strokeWidth: 4 });
  const blush =
    ellipse(80, 114, 6, 4, { fill: UI.flowerPink, stroke: 'none', opacity: 0.6 }) +
    ellipse(120, 114, 6, 4, { fill: UI.flowerPink, stroke: 'none', opacity: 0.6 });
  return svgDoc(200, 200, body + eyes + mouth + blush);
}

// ── Refresh / reset arrow icon (blue circular arrow) ─────────────────────────────
export function refreshIcon(): string {
  const arc = path('M138,72 A50,50 0 1 0 150,108', { fill: 'none', stroke: UI.refresh, strokeWidth: 15 });
  const head = polygon([[132, 52], [150, 76], [120, 80]], { fill: UI.refresh, stroke: 'none' });
  return svgDoc(200, 200, arc + head);
}

// ── Gentle "try again" icon (soft, never scary) ─────────────────────────────────
export function tryAgain(): string {
  const pad = circle(100, 100, 86, { fill: UI.refresh, stroke: 'none', opacity: 0.16 });
  const arc = path('M138,72 A50,50 0 1 0 150,108', { fill: 'none', stroke: UI.refresh, strokeWidth: 15 });
  const head = polygon([[132, 52], [150, 76], [120, 80]], { fill: UI.refresh, stroke: 'none' });
  return svgDoc(200, 200, pad + arc + head);
}

// ── Sound (speaker) icon ────────────────────────────────────────────────────────
export function soundIcon(): string {
  const box = path('M64,86 h16 l24,-22 v72 l-24,-22 h-16 z', {
    fill: UI.sound,
    stroke: UI.soundDark,
    strokeWidth: 5,
  });
  const waves = path('M118,80 a26,26 0 0 1 0,40 M132,68 a44,44 0 0 1 0,64', {
    fill: 'none',
    stroke: UI.sound,
    strokeWidth: 8,
  });
  return svgDoc(200, 200, box + waves);
}

// ── Bell (serve) icon — a hand-bell on a gold disc ───────────────────────────────
export function bell(): string {
  const disc = circle(100, 100, 74, { fill: UI.bell, stroke: UI.bellDark });
  const knob = circle(100, 56, 9, { fill: UI.bellDark, strokeWidth: 5 });
  const body = path('M66,128 C66,90 78,66 100,66 C122,66 134,90 134,128 Z', {
    fill: UI.bellBody,
    strokeWidth: 6,
  });
  const base = rect(58, 126, 84, 13, 6.5, { fill: UI.bellDark, strokeWidth: 5 });
  const clapper = circle(100, 150, 9, { fill: UI.bellDark, strokeWidth: 5 });
  const shine = path('M86,92 q6,-14 20,-12', { fill: 'none', stroke: '#ffffff', strokeWidth: 6, opacity: 0.8 });
  return svgDoc(200, 200, disc + body + base + clapper + knob + shine);
}

// ── Home icon (return to the start menu) ─────────────────────────────────────────
export function homeIcon(): string {
  const body = rect(58, 96, 84, 78, 12, { fill: '#ffffff', stroke: INK });
  const roof = polygon([[34, 104], [100, 44], [166, 104]], { fill: UI.awning, stroke: INK, strokeWidth: 6 });
  const door = rect(86, 130, 28, 44, 8, { fill: UI.awning, stroke: INK, strokeWidth: 4 });
  return svgDoc(200, 200, body + roof + door);
}

// ── Ingredient tile (rounded-square frame; ingredient image placed on top) ───────
export function tile(): string {
  const outer = rect(6, 6, 132, 132, 28, { fill: UI.tileEdge });
  const inner = rect(12, 12, 120, 120, 24, { fill: UI.tile });
  return svgDoc(144, 144, outer + inner);
}

// ── Coin price-board (hanging plaque; coins drawn on top) ───────────────────────
export function coinBoard(): string {
  const plaque = rect(10, 24, 320, 100, 26, { fill: UI.board, stroke: UI.boardEdge, strokeWidth: 6 });
  const hang =
    rect(60, 6, 12, 24, 6, { fill: UI.boardEdge, stroke: 'none' }) +
    rect(268, 6, 12, 24, 6, { fill: UI.boardEdge, stroke: 'none' });
  return svgDoc(340, 132, hang + plaque);
}

// ── Coin slot-card (where coins are placed during payment) ──────────────────────
export function coinCard(): string {
  const card = rect(10, 10, 300, 150, 26, { fill: UI.panel, stroke: UI.panelEdge, strokeWidth: 6 });
  // little scalloped top edge
  const crown: string[] = [];
  for (let x = 44; x < 290; x += 40) crown.push(circle(x, 10, 16, { fill: UI.panel, stroke: UI.panelEdge, strokeWidth: 5 }));
  return svgDoc(320, 172, crown.join('') + card);
}

// ── Earnings jar (glass + pink lid w/ flower; coins drawn inside) ────────────────
export function jar(): string {
  const glassG = linear('jarG', [[0, UI.jarGlass], [1, UI.jarGlassShade]]);
  const glass = path(
    'M 30 56 Q 30 44 46 42 L 134 42 Q 150 44 150 56 L 156 178 Q 156 198 134 198 ' +
      'L 46 198 Q 24 198 24 178 Z',
    { fill: url('jarG'), opacity: 0.92 },
  );
  const shine = path('M 48 72 Q 42 140 54 184', { fill: 'none', stroke: '#ffffff', strokeWidth: 6, opacity: 0.6 });
  const lid = rect(28, 18, 124, 30, 14, { fill: UI.jarLid });
  const lidTop = ellipse(90, 18, 62, 12, { fill: UI.jarLid });
  const flower = group(flowerMark(0, 0, 12), 'translate(90 18) scale(1)');
  return svgDoc(180, 220, defs(glassG) + glass + shine + lid + lidTop + flower);
}

export function uiTextures(
  bgW: number,
  bgH: number,
): Record<string, { svg: string; w: number; h: number }> {
  return {
    bg: { svg: background(bgW, bgH), w: bgW, h: bgH },
    bubble: { svg: bubble(), w: 360, h: 290 },
    star: { svg: star(), w: 200, h: 200 },
    refresh_icon: { svg: refreshIcon(), w: 200, h: 200 },
    try_again: { svg: tryAgain(), w: 200, h: 200 },
    sound_icon: { svg: soundIcon(), w: 200, h: 200 },
    home_icon: { svg: homeIcon(), w: 200, h: 200 },
    bell: { svg: bell(), w: 200, h: 200 },
    tile: { svg: tile(), w: 144, h: 144 },
    coin_board: { svg: coinBoard(), w: 340, h: 132 },
    coin_card: { svg: coinCard(), w: 320, h: 172 },
    jar: { svg: jar(), w: 180, h: 220 },
  };
}
